import React from 'react';
import { View, StyleSheet, Pressable, Platform } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '@/theme';
import { ThemedText, Body, Caption } from '@/components/ui/Text';
import { Button } from '@/components/ui/Button';
import type { TooltipPosition } from '@/data/tutorialSteps';

type TutorialTooltipProps = {
  title: string;
  description: string;
  position: TooltipPosition;
  step: number;
  totalSteps: number;
  onNext?: () => void;
  onPrevious?: () => void;
  onSkip: () => void;
  onGetStarted?: () => void;
  showGetStarted?: boolean;
  showPrevious?: boolean;
  style?: any;
};

export function TutorialTooltip({
  title,
  description,
  position,
  step,
  totalSteps,
  onNext,
  onPrevious,
  onSkip,
  onGetStarted,
  showGetStarted = false,
  showPrevious = false,
  style,
}: TutorialTooltipProps) {
  const theme = useTheme();
  const insets = useSafeAreaInsets();

  const isCenter = position === 'center';
  const isLastStep = step === totalSteps - 1;

  const tooltipStyle = [
    styles.tooltip,
    {
      backgroundColor: theme.colors.surface,
      borderColor: theme.colors.borderMuted,
      ...theme.shadows.level3,
    },
    isCenter && styles.centerTooltip,
    style,
  ];

  return (
    <View style={tooltipStyle}>
      {/* Progress indicator */}
      <View style={styles.progressContainer}>
        <View style={styles.progressBar}>
          <View
            style={[
              styles.progressFill,
              {
                backgroundColor: theme.colors.primary,
                width: `${((step + 1) / totalSteps) * 100}%`,
              },
            ]}
          />
        </View>
        <Caption color={theme.colors.textSecondary} style={styles.progressText}>
          {step + 1} of {totalSteps}
        </Caption>
      </View>

      {/* Content */}
      <View style={styles.content}>
        <ThemedText variant="title" style={styles.title}>
          {title}
        </ThemedText>
        <Body color={theme.colors.textSecondary} style={styles.description}>
          {description}
        </Body>
      </View>

      {/* Actions */}
      <View style={styles.actions}>
        <Pressable
          onPress={onSkip}
          style={({ pressed }) => [
            styles.skipButton,
            { opacity: pressed ? 0.7 : 1 },
          ]}
        >
          <Body color={theme.colors.textSecondary} style={styles.skipText}>
            Skip
          </Body>
        </Pressable>

        <View style={styles.navigationButtons}>
          {showPrevious && onPrevious && (
            <Button
              label="Previous"
              variant="secondary"
              size="small"
              hapticOnPressIn={false}
              onPress={onPrevious}
              style={styles.navButton}
            />
          )}
          {showGetStarted && onGetStarted ? (
            <Button
              label="Get Started"
              variant="primary"
              size="small"
              hapticOnPressIn={false}
              onPress={onGetStarted}
              style={styles.navButton}
            />
          ) : onNext ? (
            <Button
              label="Next"
              variant="primary"
              size="small"
              hapticOnPressIn={false}
              onPress={onNext}
              style={styles.navButton}
            />
          ) : null}
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  tooltip: {
    borderRadius: 16,
    borderWidth: 1,
    padding: 20,
    maxWidth: 360,
    minWidth: 280,
    ...Platform.select({
      ios: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.15,
        shadowRadius: 12,
      },
      android: {
        elevation: 8,
      },
    }),
  },
  centerTooltip: {
    alignSelf: 'center',
    marginHorizontal: 20,
  },
  progressContainer: {
    marginBottom: 16,
    gap: 8,
  },
  progressBar: {
    height: 4,
    backgroundColor: 'rgba(0, 0, 0, 0.1)',
    borderRadius: 2,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    borderRadius: 2,
  },
  progressText: {
    fontSize: 12,
    textAlign: 'right',
  },
  content: {
    marginBottom: 20,
    gap: 8,
  },
  title: {
    fontSize: 20,
    fontWeight: '700',
    marginBottom: 4,
  },
  description: {
    fontSize: 15,
    lineHeight: 22,
  },
  actions: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 12,
  },
  skipButton: {
    paddingVertical: 8,
    paddingHorizontal: 12,
  },
  skipText: {
    fontSize: 15,
    fontWeight: '500',
  },
  navigationButtons: {
    flexDirection: 'row',
    gap: 8,
    flex: 1,
    justifyContent: 'flex-end',
  },
  navButton: {
    minWidth: 100,
  },
});

